import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { api } from "$convex/convexApi";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Checkbox } from "@/components/ui/checkbox";
import { ComboboxSelect, type ComboboxOption } from "@/components/ui/combobox-select";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserProfile } from "@/hooks/useUserProfile";
import { exportToCSV, type ExportColumn } from "@/lib/export";
import { formatDate, formatStatusLabel } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";
import { getOrderStatusBadgeClass } from "@/lib/status-tag-colors";
import { OrdersTab } from "@/pages/admin/components/OrdersTab";
import { SalesOrdersTable } from "@/pages/sales/orders/page";
import { ClientReportButton } from "./ClientReportButton";
import { ContractComparisonBuilder } from "./ContractComparisonBuilder";
import { Bar, BarChart, CartesianGrid, Cell, Label, Pie, PieChart, XAxis, YAxis } from "recharts";

const DATE_RANGES = [
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "6mo", label: "Last 6 months" },
  { value: "1y", label: "Last year" },
  { value: "all", label: "All time" },
] as const;

const CONTRACT_GROUP_BY = [
  { value: "month", label: "By month" },
  { value: "week", label: "By week" },
  { value: "product", label: "By product" },
  { value: "cargo", label: "By cargo" },
] as const;

const BOX_SALES_VIEWS = [
  { value: "summary", label: "Summary" },
  { value: "byClient", label: "By client" },
  { value: "byOrigin", label: "By origin" },
  { value: "byProduct", label: "By product" },
  { value: "byCargo", label: "By cargo" },
] as const;

const ANALYTICS_TABS = [
  { value: "overview", label: "Overview" },
  { value: "inventory", label: "Inventory" },
  { value: "pricing", label: "Pricing" },
  { value: "sampleImpact", label: "Sample Impact" },
] as const;

const DATE_RANGE_DESCRIPTIONS: Record<string, string> = {
  "30d": "Recent month performance",
  "90d": "Quarter-to-date trend",
  "6mo": "Half-year demand profile",
  "1y": "Year-over-year context",
  all: "Complete historical data",
};

const ORDER_SCOPE_OPTIONS = [
  { value: "orders", label: "Completed Orders" },
  { value: "abandoned", label: "Abandoned Carts" },
  { value: "both", label: "All Orders" },
] as const;

type OrderScope = (typeof ORDER_SCOPE_OPTIONS)[number]["value"];

const DATE_RANGE_OPTIONS: ComboboxOption[] = DATE_RANGES.map((range) => ({
  value: range.value,
  label: range.label,
  sublabel: DATE_RANGE_DESCRIPTIONS[range.value],
}));

type DateRange = (typeof DATE_RANGES)[number]["value"];
type ContractGroupBy = (typeof CONTRACT_GROUP_BY)[number]["value"];
type BoxSalesView = (typeof BOX_SALES_VIEWS)[number]["value"];
type AnalyticsTab = (typeof ANALYTICS_TABS)[number]["value"];
type AnalyticsAudience = "admin" | "sales";

const MONEY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const SALES_CHART_CONFIG = {
  bags: {
    label: "Units sold",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

const REVENUE_MIX_CHART_CONFIG = {
  contractRevenue: {
    label: "Contract",
    color: "var(--chart-1)",
  },
  spotRevenue: {
    label: "Spot",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

const PIE_PALETTE = [
  "hsl(221 83% 53%)",   // blue
  "hsl(142 71% 45%)",   // green
  "hsl(262 83% 58%)",   // purple
  "hsl(31 97% 52%)",    // amber
  "hsl(346 77% 50%)",   // rose
  "hsl(189 94% 43%)",   // cyan
  "hsl(47 96% 53%)",    // yellow
  "hsl(280 60% 55%)",   // violet
  "hsl(160 60% 40%)",   // teal
  "hsl(12 76% 61%)",    // coral
  "hsl(200 70% 50%)",   // sky
  "hsl(330 65% 60%)",   // pink
  "hsl(90 55% 45%)",    // lime
  "hsl(0 70% 55%)",     // red
  "hsl(50 80% 45%)",    // gold
  "hsl(240 55% 60%)",   // indigo
];

const PANEL_CARD_CLASS = "border-border/70 bg-card shadow-none";
const FILTER_CARD_CLASS = "rounded-lg border border-border/70 bg-muted/20 p-4";

function getVelocityBadgeClass(days: number): string {
  if (days < 30) return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  if (days < 60) return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
}

function getSampleConversionBadgeClass(status: unknown): string {
  const normalized = typeof status === "string" ? status.toLowerCase() : "";
  if (normalized === "target_converted") {
    return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  }
  if (normalized === "bought_other") {
    return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  }
  return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-300";
}

function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function toInt(value: string, defaultValue: number): number {
  const parsed = Number.parseInt(value, 10);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return defaultValue;
}

function formatMoney(value: unknown): string {
  return MONEY_FORMATTER.format(toNumber(value));
}

function formatMetricNumber(value: unknown, maximumFractionDigits = 2): string {
  return toNumber(value).toLocaleString("en-US", { maximumFractionDigits });
}

function formatUnits(value: unknown): string {
  return `${formatMetricNumber(value)} units`;
}

function formatShortDate(value: unknown): string {
  if (typeof value === "number" || typeof value === "string" || value instanceof Date) {
    return formatDate(value, { month: "short", day: "numeric", year: "numeric" });
  }
  return "-";
}

function computeDateRange(range: string): { from: string | null; to: string | null } {
  if (range === "all" || !range) return { from: null, to: null };
  const now = new Date();
  const to = now.toISOString().split("T")[0];
  let daysBack: number;
  switch (range) {
    case "30d": daysBack = 30; break;
    case "90d": daysBack = 90; break;
    case "6mo": daysBack = 180; break;
    case "1y": daysBack = 365; break;
    default: return { from: null, to: null };
  }
  const fromDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
  return { from: fromDate.toISOString().split("T")[0] ?? null, to: to ?? null };
}

function exportRows(rows: Record<string, unknown>[], columns: ExportColumn[], filename: string) {
  if (rows.length === 0) return;
  exportToCSV(rows, columns, filename);
}

function useSampleImpactColumns() {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "sampleEventAt",
        accessorFn: (row) => row.sampleEventAt ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Sample Date" />,
        cell: ({ row }) => <span className="text-xs">{formatShortDate(row.original.sampleEventAt)}</span>,
        meta: { label: "Sample Date", variant: "number", placeholder: "Min timestamp" },
        enableColumnFilter: true,
      },
      {
        id: "client",
        accessorFn: (row) => row.clientName || row.clientCompany || row.clientEmail || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Client" />,
        cell: ({ row }) => (
          <div className="min-w-[200px]">
            <p className="font-medium">{row.original.clientName || "Unknown client"}</p>
            <p className="text-xs text-muted-foreground">{row.original.clientCompany || row.original.clientEmail || "-"}</p>
          </div>
        ),
        meta: { label: "Client", placeholder: "Search client...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "clientIsEnterprise",
        accessorFn: (row) => Boolean(row.clientIsEnterprise),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Enterprise" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getEnterpriseBadgeClass(row.original.clientIsEnterprise)}
          >
            {getEnterpriseBadgeLabel(row.original.clientIsEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select",
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some(
            (value: string) =>
              value ===
              String(Boolean(row.original.clientIsEnterprise)),
          ),
      },
      {
        id: "target",
        accessorFn: (row) => row.targetCargoOci || row.targetProductName || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Target" />,
        cell: ({ row }) => (
          <span className="text-xs">
            {row.original.targetCargoOci
              ? `OCI ${row.original.targetCargoOci}`
              : row.original.targetProductName || "Unlinked sample"}
          </span>
        ),
        meta: { label: "Target", placeholder: "Search target...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "preWindowRevenue",
        accessorFn: (row) => toNumber(row.preWindowRevenue),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Pre Revenue" />,
        cell: ({ row }) => (
          <span>
            {formatMoney(row.original.preWindowRevenue)} ({toNumber(row.original.preWindowOrders).toLocaleString()} orders)
          </span>
        ),
        meta: { label: "Pre Revenue", variant: "number", placeholder: "Min pre revenue" },
        enableColumnFilter: true,
      },
      {
        id: "postWindowRevenue",
        accessorFn: (row) => toNumber(row.postWindowRevenue),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Post Revenue" />,
        cell: ({ row }) => (
          <span>
            {formatMoney(row.original.postWindowRevenue)} ({toNumber(row.original.postWindowOrders).toLocaleString()} orders)
          </span>
        ),
        meta: { label: "Post Revenue", variant: "number", placeholder: "Min post revenue" },
        enableColumnFilter: true,
      },
      {
        id: "conversionOutcome",
        accessorFn: (row) => row.conversionOutcome || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Conversion" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getSampleConversionBadgeClass(row.original.conversionOutcome)}>
            {row.original.conversionOutcome === "target_converted"
              ? "Target Converted"
              : row.original.conversionOutcome === "bought_other"
                ? "Bought Other"
                : "No Purchase"}
          </Badge>
        ),
        meta: {
          label: "Conversion",
          variant: "select",
          options: [
            { label: "Target Converted", value: "target_converted" },
            { label: "Bought Other", value: "bought_other" },
            { label: "No Purchase", value: "no_purchase" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "postWindowTopProducts",
        accessorFn: (row) => (row.postWindowTopProducts ?? []).map((item: any) => item.productName).join(" • "),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Top Purchases After" />,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {(row.original.postWindowTopProducts ?? []).slice(0, 2).map((item: any) => item.productName).join(" • ") || "-"}
          </span>
        ),
        meta: { label: "Top Purchases", placeholder: "Search product...", variant: "text" },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

function formatSalesUnitLabel(unitType: unknown, unitsSold: unknown): string {
  const count = toNumber(unitsSold);
  const normalized = unitType === "boxes" ? "boxes" : "bags";
  const singular = normalized === "boxes" ? "box" : "bag";
  return `${count.toLocaleString()} ${count === 1 ? singular : normalized}`;
}

function SalesByOriginExpandedRow({ row }: { row: { original: any } }) {
  const breakdownRows = ((row.original.productBreakdown ?? []) as any[]).slice();

  if (breakdownRows.length === 0) {
    return (
      <p className="px-1 py-3 text-sm text-muted-foreground">
        No coffee-level sales detail is available for this origin.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">{row.original.originName}</Badge>
        <span>{toNumber(row.original.totalBags).toLocaleString()} total units sold</span>
        <span>{formatMoney(row.original.totalRevenue)} revenue</span>
      </div>
      <div className="overflow-x-auto rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="py-2 pr-4">Coffee Type</TableHead>
              <TableHead className="py-2 pr-4">Bags / Boxes</TableHead>
              <TableHead className="py-2 pr-4">Purchase Contract</TableHead>
              <TableHead className="py-2 pr-4">OCI</TableHead>
              <TableHead className="py-2 text-right">Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {breakdownRows.map((item: any) => (
              <TableRow key={item.key ?? `${item.productId}-${item.purchaseContractNumber ?? "none"}-${item.unitType ?? "bags"}`}>
                <TableCell className="py-2 pr-4 font-medium">{item.productName || "-"}</TableCell>
                <TableCell className="py-2 pr-4">
                  <Badge variant="outline">
                    {formatSalesUnitLabel(item.unitType, item.unitsSold ?? item.bags)}
                  </Badge>
                </TableCell>
                <TableCell className="py-2 pr-4 font-mono text-xs">
                  {item.purchaseContractNumber || "-"}
                </TableCell>
                <TableCell className="py-2 pr-4 font-mono text-xs">
                  {item.ociNumber || "-"}
                </TableCell>
                <TableCell className="py-2 text-right">{formatMoney(item.revenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function useSalesByOriginColumns() {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "originName",
        accessorFn: (row) => row.originName || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Origin" />,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-0 py-0 font-medium"
            onClick={() => row.toggleExpanded()}
          >
            {row.original.originName}
          </Button>
        ),
        meta: { label: "Origin", placeholder: "Search origin...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorFn: (row) => toNumber(row.totalBags),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Units" />,
        cell: ({ row }) => <span>{toNumber(row.original.totalBags).toLocaleString()}</span>,
        meta: { label: "Units", placeholder: "Min units", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalRevenue",
        accessorFn: (row) => toNumber(row.totalRevenue),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Revenue" />,
        cell: ({ row }) => <span>{formatMoney(row.original.totalRevenue)}</span>,
        meta: { label: "Revenue", placeholder: "Min revenue", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "contractBags",
        accessorFn: (row) => toNumber(row.contractBags),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Contract" />,
        cell: ({ row }) => <span>{toNumber(row.original.contractBags).toLocaleString()}</span>,
        meta: { label: "Contract", placeholder: "Min contract", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "spotBags",
        accessorFn: (row) => toNumber(row.spotBags),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Spot" />,
        cell: ({ row }) => <span>{toNumber(row.original.spotBags).toLocaleString()}</span>,
        meta: { label: "Spot", placeholder: "Min spot", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "topProducts",
        accessorFn: (row) =>
          (row.productBreakdown ?? []).map((p: any) => p.productName).join(" • "),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Top Coffee Types" />,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {(row.original.productBreakdown ?? [])
              .slice(0, 3)
              .map((p: any) => p.productName)
              .join(" • ") || "-"}
          </span>
        ),
        meta: { label: "Top Coffee Types", placeholder: "Search coffee...", variant: "text" },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

function useProductGrossIncomeColumns() {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "productName",
        accessorFn: (row) => row.productName || "",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Product" />,
        cell: ({ row }) => <span className="font-medium">{row.original.productName}</span>,
        meta: { label: "Product", placeholder: "Search product...", variant: "text" },
        enableColumnFilter: true,
      },
      {
        id: "totalPounds",
        accessorFn: (row) => toNumber(row.totalPounds),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Lbs Sold" />,
        cell: ({ row }) => <span className="tabular-nums">{formatMetricNumber(row.original.totalPounds, 0)}</span>,
        meta: { label: "Lbs Sold", placeholder: "Min lbs", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorFn: (row) => toNumber(row.totalBags),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Units" />,
        cell: ({ row }) => <span className="tabular-nums">{formatMetricNumber(row.original.totalBags, 0)}</span>,
        meta: { label: "Units", placeholder: "Min units", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "totalRevenue",
        accessorFn: (row) => toNumber(row.totalRevenue),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Sales" />,
        cell: ({ row }) => <span className="tabular-nums">{formatMoney(row.original.totalRevenue)}</span>,
        meta: { label: "Sales", placeholder: "Min sales", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "costOfGoodsSold",
        accessorFn: (row) => (row.costOfGoodsSold == null ? Number.NEGATIVE_INFINITY : toNumber(row.costOfGoodsSold)),
        header: ({ column }) => <DataTableColumnHeader column={column} label="COGS" />,
        cell: ({ row }) => row.original.costOfGoodsSold == null ? (
          <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
            Missing cost
          </Badge>
        ) : (
          <span className="tabular-nums">{formatMoney(row.original.costOfGoodsSold)}</span>
        ),
        meta: { label: "COGS", placeholder: "Min COGS", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "grossIncome",
        accessorFn: (row) => (row.grossIncome == null ? Number.NEGATIVE_INFINITY : toNumber(row.grossIncome)),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Gross Income" />,
        cell: ({ row }) => row.original.grossIncome == null ? (
          <span className="text-xs text-muted-foreground">Set cargo cost to calculate</span>
        ) : (
          <span className="font-medium tabular-nums">{formatMoney(row.original.grossIncome)}</span>
        ),
        meta: { label: "Gross Income", placeholder: "Min gross income", variant: "number" },
        enableColumnFilter: true,
      },
      {
        id: "grossMarginPercent",
        accessorFn: (row) => (row.grossMarginPercent == null ? Number.NEGATIVE_INFINITY : toNumber(row.grossMarginPercent)),
        header: ({ column }) => <DataTableColumnHeader column={column} label="Gross Margin" />,
        cell: ({ row }) => row.original.grossMarginPercent == null ? (
          <span className="text-xs text-muted-foreground">N/A</span>
        ) : (
          <span className="tabular-nums">{toNumber(row.original.grossMarginPercent).toFixed(1)}%</span>
        ),
        meta: { label: "Gross Margin", placeholder: "Min margin %", variant: "number" },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

const VALID_DATE_RANGES = new Set<string>(DATE_RANGES.map((d) => d.value));
const VALID_ANALYTICS_TABS = new Set<string>(ANALYTICS_TABS.map((t) => t.value));

function useProductItemColumns() {
  return React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "orderNumber",
        accessorKey: "orderNumber",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Order #" />,
        cell: ({ row }) => <span className="text-xs font-mono font-medium">{row.original.orderNumber}</span>,
        meta: { label: "Order", placeholder: "Search order...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "customer",
        accessorFn: (row) => row.company || row.customer,
        header: ({ column }) => <DataTableColumnHeader column={column} label="Customer" />,
        cell: ({ row }) => <span className="text-xs font-medium">{row.original.company || row.original.customer}</span>,
        meta: { label: "Customer", placeholder: "Search...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorKey: "isEnterprise",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Enterprise" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getEnterpriseBadgeClass(row.original.isEnterprise)}>
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) =>
          filterValues.some((val: string) => val === String(row.original.isEnterprise)),
      },
      {
        id: "cargoOci",
        accessorKey: "cargoOci",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Cargo" />,
        cell: ({ row }) => <span className="text-xs font-mono">{row.original.cargoOci}</span>,
        meta: { label: "Cargo", placeholder: "Search cargo...", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "quantity",
        accessorKey: "quantity",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Qty" />,
        cell: ({ row }) => <span className="text-xs tabular-nums">{row.original.quantity}</span>,
        meta: { label: "Qty", variant: "number" as const },
      },
      {
        id: "unitPrice",
        accessorKey: "unitPrice",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Unit $/lb" />,
        cell: ({ row }) => <span className="text-xs tabular-nums">{formatMoney(row.original.unitPrice)}</span>,
        meta: { label: "Unit Price", variant: "number" as const },
      },
      {
        id: "lineTotal",
        accessorKey: "lineTotal",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Line Total" />,
        cell: ({ row }) => <span className="text-xs font-medium tabular-nums">{formatMoney(row.original.lineTotal)}</span>,
        meta: { label: "Line Total", variant: "number" as const },
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
        cell: ({ row }) => (
          <Badge variant="outline" className={getOrderStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select" as const,
          options: [
            { label: "Delivered", value: "delivered" },
            { label: "In Transit", value: "in_transit" },
            { label: "Ready for Pickup", value: "ready_for_pickup" },
            { label: "Ready to Prepare", value: "ready_to_prepare" },
            { label: "Awaiting Inventory", value: "awaiting_inventory" },
            { label: "Pending Payment", value: "pending_payment" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => <DataTableColumnHeader column={column} label="Created" />,
        cell: ({ row }) => <span className="text-xs">{row.original.createdAt ? formatShortDate(row.original.createdAt) : "-"}</span>,
        meta: { label: "Created", variant: "date" as const },
        enableColumnFilter: true,
      },
    ],
    [],
  );
}

function ProductItemsSection({
  authUserId,
  productId,
  productName,
}: {
  authUserId: string;
  productId: string;
  productName: string;
}) {
  // Dedicated server-side query: fetches order items by product using the
  // by_product index (1 index range read), then batch-fetches parent orders
  // and user profiles. No N*M explosion.
  const rawItems = useQuery(
    api.admin.getOrderItemsByProduct,
    { authUserId, productId },
  );
  const columns = useProductItemColumns();
  const isLoading = rawItems === undefined;

  const productItems = React.useMemo(() => {
    if (!rawItems || !Array.isArray(rawItems)) return [];
    return rawItems.map((item: any, index: number) => {
      const qty = toNumber(item.quantity);
      const price = toNumber(item.unitPrice);
      const weight = toNumber(item.unitWeight);
      return {
        id: item._id || `pi-${index}`,
        orderNumber: item.orderNumber || "-",
        customer: item.customer || "Unknown",
        company: item.company || "",
        isEnterprise: item.isEnterprise || false,
        cargoOci: item.cargo?.ociNumber || "-",
        quantity: qty,
        unitPrice: price,
        unitWeight: weight,
        lineTotal: qty * price * weight,
        status: item.orderStatus || "unknown",
        createdAt: item.orderCreatedAt || 0,
      };
    });
  }, [rawItems]);

  return (
    <Card className={PANEL_CARD_CLASS}>
      <CardHeader>
        <CardTitle>Product Order Items</CardTitle>
        <CardDescription>
          All order line items for {productName} ({productItems.length} found)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SectionTableSkeleton rows={8} cols={7} />
        ) : productItems.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No order items found for this product.</p>
        ) : (
          <InternalEntityDataTable
            data={productItems}
            columns={columns}
            pageSize={20}
            enableSelection={false}
            initialSorting={[{ id: "createdAt", desc: true }]}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminAnalyticsPage({
  audience = "admin",
}: {
  audience?: AnalyticsAudience;
}) {
  const { authUserId, userRole, isProfileLoading } = useUserProfile();
  const isSalesAudience = audience === "sales";
  const hasAnalyticsAccess =
    userRole === "admin" ||
    (isSalesAudience && (userRole === "sales" || userRole === "marketing"));
  const canUseAnalyticsQueries = Boolean(authUserId && hasAnalyticsAccess);
  const canUseAdminOrderQueries = Boolean(authUserId && userRole === "admin");
  const isAuthResolving = isProfileLoading || (Boolean(authUserId) && userRole === undefined);

  const [searchParams, setSearchParams] = useSearchParams();
  const availableAnalyticsTabs = React.useMemo(
    () =>
      isSalesAudience && userRole === "sales"
        ? ANALYTICS_TABS.filter((tab) => tab.value === "overview")
        : ANALYTICS_TABS,
    [isSalesAudience, userRole],
  );
  const availableAnalyticsTabValues = React.useMemo(
    () => new Set<string>(availableAnalyticsTabs.map((tab) => tab.value)),
    [availableAnalyticsTabs],
  );

  // ── URL-synced filter state (primary filters) ──
  const dateRange: DateRange = (() => {
    const v = searchParams.get("dateRange");
    return v && VALID_DATE_RANGES.has(v) ? (v as DateRange) : "all";
  })();
  const requestedAnalyticsTab = searchParams.get("tab");
  const analyticsTab: AnalyticsTab = (() => {
    return requestedAnalyticsTab && availableAnalyticsTabValues.has(requestedAnalyticsTab)
      ? (requestedAnalyticsTab as AnalyticsTab)
      : "overview";
  })();
  const selectedUserProfileId = searchParams.get("clientId") || "all-users";
  const comparisonCampaignId = searchParams.get("comparisonCampaignId") ?? undefined;
  const [comparisonOpen, setComparisonOpen] = React.useState(Boolean(comparisonCampaignId));
  const selectedProductId = searchParams.get("productId") || "all-products";
  const cargoSearch = searchParams.get("cargoSearch") || "";
  const contractSearch = searchParams.get("contractSearch") || "";
  const deliveredOnly = searchParams.get("deliveredOnly") === "true";
  const orderScope: OrderScope = (() => {
    const v = searchParams.get("orderScope");
    return v === "abandoned" || v === "both" ? v : "orders";
  })();

  const updateSearchParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") params.delete(key);
        else params.set(key, value);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setAnalyticsTab = React.useCallback(
    (value: string) => updateSearchParams({ tab: value === "overview" ? null : value }),
    [updateSearchParams],
  );
  const setDeliveredOnly = React.useCallback(
    (value: boolean) => updateSearchParams({ deliveredOnly: value ? "true" : null }),
    [updateSearchParams],
  );
  const setOrderScope = React.useCallback(
    (value: string) => updateSearchParams({ orderScope: value === "orders" ? null : value }),
    [updateSearchParams],
  );
  const setCargoSearch = React.useCallback(
    (value: string) => updateSearchParams({ cargoSearch: (value && value !== "all-cargos") ? value.trim() : null }),
    [updateSearchParams],
  );
  const setContractSearch = React.useCallback(
    (value: string) => updateSearchParams({ contractSearch: (value && value !== "all-contracts") ? value.trim() : null }),
    [updateSearchParams],
  );

  React.useEffect(() => {
    if (requestedAnalyticsTab && !availableAnalyticsTabValues.has(requestedAnalyticsTab)) {
      updateSearchParams({ tab: null });
    }
  }, [availableAnalyticsTabValues, requestedAnalyticsTab, updateSearchParams]);

  // ── Local-only filter state (secondary controls) ──
  const [contractGroupBy, setContractGroupBy] = React.useState<ContractGroupBy>("month");
  const [boxSalesView, setBoxSalesView] = React.useState<BoxSalesView>("summary");
  const [inventoryAgeThresholdInput, setInventoryAgeThresholdInput] = React.useState("90");
  const [sampleLookbackDaysInput, setSampleLookbackDaysInput] = React.useState("90");
  const [sampleLookforwardDaysInput, setSampleLookforwardDaysInput] = React.useState("90");
  const [cargoPieDateRange, setCargoPieDateRange] = React.useState<DateRange>("30d");

  const inventoryAgeThreshold = React.useMemo(
    () => toInt(inventoryAgeThresholdInput, 90),
    [inventoryAgeThresholdInput],
  );
  const sampleLookbackDays = React.useMemo(
    () => toInt(sampleLookbackDaysInput, 90),
    [sampleLookbackDaysInput],
  );
  const sampleLookforwardDays = React.useMemo(
    () => toInt(sampleLookforwardDaysInput, 90),
    [sampleLookforwardDaysInput],
  );
  const isOverviewTab = analyticsTab === "overview";
  const isInventoryTab = analyticsTab === "inventory";
  const isPricingTab = analyticsTab === "pricing";
  const isSampleImpactTab = analyticsTab === "sampleImpact";

  const shouldLoadOverview = canUseAnalyticsQueries && isOverviewTab;
  const shouldLoadInventory = canUseAnalyticsQueries && isInventoryTab;
  const shouldLoadPricing = canUseAnalyticsQueries && isPricingTab;
  const shouldLoadSampleImpact = canUseAnalyticsQueries && isSampleImpactTab;

  const users = useQuery(
    api.sales.getAllUsersForImpersonation,
    canUseAnalyticsQueries ? { authUserId: authUserId! } : "skip",
  );

  const products = useQuery(api.products.listProducts);

  const cargoFilterOptions = useQuery(
    api.analytics.getCargoFilterOptions,
    canUseAnalyticsQueries ? {} : "skip",
  );

  const userIdArg = selectedUserProfileId === "all-users" ? undefined : (selectedUserProfileId as any);
  const productIdArg = selectedProductId === "all-products" ? undefined : (selectedProductId as any);

  const salesByProduct = useQuery(
    api.analytics.getSalesByProduct,
    shouldLoadOverview
      ? {
          dateRange,
          userId: userIdArg,
          productId: productIdArg,
          deliveredOnly,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
          orderScope,
        }
      : "skip",
  );

  const salesByOrigin = useQuery(
    api.analytics.getSalesByOrigin,
    shouldLoadOverview
      ? {
          dateRange,
          userId: userIdArg,
          productId: productIdArg,
          deliveredOnly,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
          orderScope,
        }
      : "skip",
  );

  const orderFilterStats = useQuery(
    api.analytics.getOrderFilterStats,
    shouldLoadOverview
      ? {
          dateRange,
          userId: userIdArg,
          productId: productIdArg,
          deliveredOnly,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
          orderScope,
        }
      : "skip",
  );

  const inventoryVelocity = useQuery(
    api.analytics.getInventoryVelocity,
    shouldLoadInventory
      ? {
          productId: productIdArg,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const priceAnalytics = useQuery(
    api.analytics.getPriceAnalytics,
    shouldLoadPricing
      ? {
          dateRange,
          productId: productIdArg,
          deliveredOnly,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const contractVsSpot = useQuery(
    api.analytics.getContractVsSpotAnalysis,
    shouldLoadPricing
      ? {
          dateRange,
          productId: productIdArg,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
          groupBy: contractGroupBy,
        }
      : "skip",
  );

  const contractVsSpotByCargo = useQuery(
    api.analytics.getContractVsSpotByCargoAnalysis,
    shouldLoadPricing
      ? {
          dateRange,
          productId: productIdArg,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
          minSales: 1,
        }
      : "skip",
  );

  const revenueByPricingType = useQuery(
    api.analytics.getRevenueByPricingType,
    shouldLoadPricing
      ? {
          dateRange,
          granularity: "month",
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const boxSales = useQuery(
    api.analytics.getBoxSalesAnalysis,
    shouldLoadInventory
      ? {
          dateRange,
          userId: userIdArg,
          deliveredOnly,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const inventoryAge = useQuery(
    api.analytics.getInventoryAgeAnalysis,
    shouldLoadInventory
      ? {
          ageThresholdDays: inventoryAgeThreshold,
          spotOnly: true,
          productId: productIdArg,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const cargoStatusDistribution = useQuery(
    api.analytics.getCargoStatusDistribution,
    shouldLoadInventory
      ? {
          dateRange: cargoPieDateRange,
          cargoSearch: cargoSearch || undefined,
          contractSearch: contractSearch || undefined,
        }
      : "skip",
  );

  const dataQuality = useQuery(
    api.analytics.getAnalyticsDataQuality,
    shouldLoadPricing ? {} : "skip",
  );

  const sampleImpact = useQuery(
    api.analytics.getSampleImpactAnalysis,
    shouldLoadSampleImpact
      ? {
          dateRange,
          userId: userIdArg,
          productId: productIdArg,
          lookbackDays: sampleLookbackDays,
          lookforwardDays: sampleLookforwardDays,
        }
      : "skip",
  );

  // Light list of a single client's orders (userId-indexed, 1 index range read)
  const clientOrdersLight = useQuery(
    api.admin.getOrdersList,
    canUseAdminOrderQueries && shouldLoadOverview && selectedUserProfileId !== "all-users"
      ? {
          authUserId: authUserId!,
          userId: selectedUserProfileId,
        }
      : "skip",
  );
  // Enrich all of this client's orders (typically <100 per user)
  const clientOrderIds = React.useMemo(
    () => (clientOrdersLight ?? []).map((o: any) => o._id),
    [clientOrdersLight],
  );
  const clientEnriched = useQuery(
    api.admin.getOrdersEnriched,
    canUseAdminOrderQueries && authUserId && clientOrderIds.length > 0
      ? { authUserId, orderIds: clientOrderIds as any }
      : "skip",
  ) as Record<string, any> | undefined;
  // Merge light + enriched into the same shape the export callback expects
  const clientOrders = React.useMemo(() => {
    if (!clientOrdersLight) return undefined;
    return clientOrdersLight.map((order: any) => {
      const e = clientEnriched?.[String(order._id)];
      return { ...order, ...(e || {}) };
    });
  }, [clientOrdersLight, clientEnriched]);

  const isLoadingUsers = isAuthResolving || (canUseAnalyticsQueries && users === undefined);
  const isLoadingProducts = isAuthResolving || products === undefined;
  const isLoadingCargoFilters = isAuthResolving || (canUseAnalyticsQueries && cargoFilterOptions === undefined);
  const isLoadingSalesByProduct = isAuthResolving || (shouldLoadOverview && salesByProduct === undefined);
  const isLoadingSalesByOrigin = isAuthResolving || (shouldLoadOverview && salesByOrigin === undefined);
  const isLoadingOrderFilterStats = isAuthResolving || (shouldLoadOverview && orderFilterStats === undefined);
  const isLoadingInventoryVelocity = isAuthResolving || (shouldLoadInventory && inventoryVelocity === undefined);
  const isLoadingPriceAnalytics = isAuthResolving || (shouldLoadPricing && priceAnalytics === undefined);
  const isLoadingContractVsSpot = isAuthResolving || (shouldLoadPricing && contractVsSpot === undefined);
  const isLoadingContractVsSpotByCargo = isAuthResolving || (shouldLoadPricing && contractVsSpotByCargo === undefined);
  const isLoadingRevenueMix = isAuthResolving || (shouldLoadPricing && revenueByPricingType === undefined);
  const isLoadingBoxSales = isAuthResolving || (shouldLoadInventory && boxSales === undefined);
  const isLoadingInventoryAge = isAuthResolving || (shouldLoadInventory && inventoryAge === undefined);
  const isLoadingCargoStatus = isAuthResolving || (shouldLoadInventory && cargoStatusDistribution === undefined);
  const isLoadingDataQuality = isAuthResolving || (shouldLoadPricing && dataQuality === undefined);
  const isLoadingSampleImpact = isAuthResolving || (shouldLoadSampleImpact && sampleImpact === undefined);
  const isLoadingClientOrders =
    (isAuthResolving && selectedUserProfileId !== "all-users" && shouldLoadOverview) ||
    (shouldLoadOverview &&
    selectedUserProfileId !== "all-users" &&
    clientOrders === undefined);

  const salesByProductRows = (salesByProduct ?? []) as any[];
  const salesByOriginRows = (salesByOrigin ?? []) as any[];
  const inventoryVelocityRows = (inventoryVelocity ?? []) as any[];
  const priceAnalyticsRows = (priceAnalytics ?? []) as any[];
  const contractVsSpotRows = (contractVsSpot?.data ?? []) as any[];
  const contractVsSpotByCargoRows = (contractVsSpotByCargo?.data ?? []) as any[];
  const revenueByTypeRows = (revenueByPricingType?.data ?? []) as any[];
  const sampleImpactRows = (sampleImpact?.rows ?? []) as any[];
  const clientOrderRows = (clientOrders ?? []) as any[];
  const inventoryAgeRows = (inventoryAge?.cargos ?? []) as any[];
  const usersRows = (users ?? []) as any[];
  const productsRows = (products ?? []) as any[];
  const sampleImpactColumns = useSampleImpactColumns();
  const salesByOriginColumns = useSalesByOriginColumns();
  const productGrossIncomeColumns = useProductGrossIncomeColumns();
  const sampleImpactTableRows = React.useMemo(
    () =>
      sampleImpactRows.map((row) => ({
        ...row,
        id: row.sampleRequestId || `${row.userId}-${row.sampleEventAt}`,
      })),
    [sampleImpactRows],
  );

  const userFilterOptions = React.useMemo<ComboboxOption[]>(
    () => [
      {
        value: "all-users",
        label:
          isSalesAudience && userRole === "sales" ? "My clients" : "All clients",
        sublabel:
          isSalesAudience && userRole === "sales"
            ? "Only your assigned accounts"
            : "No client filter",
      },
      ...usersRows
        .map((user) => ({
          value: String(user._id),
          label: user.company || user.name || user.email || "Unknown client",
          sublabel: [user.email, user.company, user.tier ? `Tier ${user.tier}` : undefined]
            .filter(Boolean)
            .join(" • "),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [isSalesAudience, userRole, usersRows],
  );

  const productFilterOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "all-products", label: "All products", sublabel: "No product filter" },
      ...productsRows
        .map((product) => ({
          value: String(product._id),
          label: product.name || "Unnamed product",
          sublabel: [product.origin?.name, product.region?.name, product.producer?.name]
            .filter(Boolean)
            .join(" • "),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [productsRows],
  );

  const contractFilterOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "all-contracts", label: "All contracts / PO", sublabel: "No contract filter" },
      ...(cargoFilterOptions?.contracts ?? []).map((c: string) => ({
        value: c,
        label: c,
      })),
    ],
    [cargoFilterOptions],
  );

  const cargoOciFilterOptions = React.useMemo<ComboboxOption[]>(
    () => [
      { value: "all-cargos", label: "All cargos / OCI", sublabel: "No cargo filter" },
      ...(cargoFilterOptions?.ociNumbers ?? []).map((oci: string) => ({
        value: oci,
        label: oci,
      })),
    ],
    [cargoFilterOptions],
  );

  // ── URL-syncing filter handlers ──
  // These set both the primary param (for dropdown state) and derived params
  // (company / productName / from / to) that OrdersTab reads for scoped filtering.
  const handleDateRangeFilterChange = React.useCallback(
    (value: string) => {
      const range = (value || "all") as DateRange;
      const { from, to } = computeDateRange(range);
      updateSearchParams({
        dateRange: range === "all" ? null : range,
        from,
        to,
      });
    },
    [updateSearchParams],
  );

  const handleClientFilterChange = React.useCallback(
    (value: string) => {
      const id = value || "all-users";
      if (id === "all-users") {
        updateSearchParams({ clientId: null, company: null });
      } else {
        const user = usersRows.find((u: any) => String(u._id) === id);
        updateSearchParams({
          clientId: id,
          company: user?.company || user?.name || user?.email || null,
        });
      }
    },
    [updateSearchParams, usersRows],
  );

  const handleProductFilterChange = React.useCallback(
    (value: string) => {
      const id = value || "all-products";
      if (id === "all-products") {
        updateSearchParams({ productId: null, productName: null });
      } else {
        const product = productsRows.find((p: any) => String(p._id) === id);
        updateSearchParams({
          productId: id,
          productName: product?.name || null,
        });
      }
    },
    [updateSearchParams, productsRows],
  );

  const selectedProductName = React.useMemo(() => {
    if (selectedProductId === "all-products") return "";
    const found = productsRows.find((p) => String(p._id) === selectedProductId);
    return found?.name || "";
  }, [selectedProductId, productsRows]);

  const orderStatsScopeHint = React.useMemo(() => {
    const activeFilters = [];
    if (selectedUserProfileId !== "all-users") activeFilters.push("client");
    if (selectedProductId !== "all-products") activeFilters.push("product");
    if (cargoSearch) activeFilters.push("cargo");
    if (contractSearch) activeFilters.push("contract");
    if (deliveredOnly) activeFilters.push("delivered");
    if (orderScope !== "orders") activeFilters.push("order status");
    return activeFilters.length > 0
      ? `Filtered by ${activeFilters.join(", ")}`
      : "Across selected scope";
  }, [cargoSearch, contractSearch, deliveredOnly, orderScope, selectedProductId, selectedUserProfileId]);

  const orderStatsCountTitle = orderScope === "abandoned"
    ? "Abandoned Carts"
    : orderScope === "both"
      ? "Orders / Carts"
      : "Orders Placed";

  const orderStatsAmountTitle = orderScope === "abandoned"
    ? "Cart Amount"
    : orderScope === "both"
      ? "Order / Cart Amount"
      : "Order Amount";

  const salesChartData = React.useMemo(() => {
    const weeklyData = new Map<string, { date: Date; bags: number }>();

    for (const item of salesByProductRows) {
      const sourceDate = item?.date;
      if (!sourceDate) continue;

      const date = new Date(sourceDate);
      if (Number.isNaN(date.getTime())) continue;

      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const key = weekStart.toISOString();

      const existing = weeklyData.get(key);
      const bags = toNumber(item?.totalBags);
      if (existing) {
        existing.bags += bags;
      } else {
        weeklyData.set(key, { date: weekStart, bags });
      }
    }

    return Array.from(weeklyData.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [salesByProductRows]);

  const salesChartRows = React.useMemo(
    () =>
      salesChartData.map((point) => ({
        dateLabel: point.date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        bags: Math.round(point.bags * 100) / 100,
      })),
    [salesChartData],
  );

  const totalBagsSold = React.useMemo(
    () => salesByProductRows.reduce((sum, item) => sum + toNumber(item?.totalBags), 0),
    [salesByProductRows],
  );

  const totalPoundsSold = React.useMemo(
    () => salesByProductRows.reduce((sum, item) => sum + toNumber(item?.totalPounds), 0),
    [salesByProductRows],
  );

  const totalRevenue = React.useMemo(
    () => salesByProductRows.reduce((sum, item) => sum + toNumber(item?.totalRevenue), 0),
    [salesByProductRows],
  );

  const productPerformanceRows = React.useMemo(() => {
    const byProduct = new Map<string, {
      id: string;
      productId: string;
      productName: string;
      totalBags: number;
      totalPounds: number;
      totalRevenue: number;
      costOfGoodsSold: number;
      grossIncome: number;
      contractBags: number;
      spotBags: number;
      missingCostLineItems: number;
      missingCostPounds: number;
      hasCompleteCostData: boolean;
    }>();

    for (const item of salesByProductRows) {
      if (!item?.productId && !item?.productName) continue;
      const productId = String(item.productId ?? item.productName);
      const existing = byProduct.get(productId) ?? {
        id: productId,
        productId,
        productName: String(item.productName ?? productId),
        totalBags: 0,
        totalPounds: 0,
        totalRevenue: 0,
        costOfGoodsSold: 0,
        grossIncome: 0,
        contractBags: 0,
        spotBags: 0,
        missingCostLineItems: 0,
        missingCostPounds: 0,
        hasCompleteCostData: true,
      };

      existing.totalBags += toNumber(item?.totalBags);
      existing.totalPounds += toNumber(item?.totalPounds);
      existing.totalRevenue += toNumber(item?.totalRevenue);
      existing.contractBags += toNumber(item?.contractBags);
      existing.spotBags += toNumber(item?.spotBags);

      const hasCompleteCostData = item?.hasCompleteCostData !== false && item?.costOfGoodsSold != null && item?.grossIncome != null;
      if (hasCompleteCostData) {
        existing.costOfGoodsSold += toNumber(item?.costOfGoodsSold);
        existing.grossIncome += toNumber(item?.grossIncome);
      } else {
        existing.hasCompleteCostData = false;
        existing.missingCostLineItems += toNumber(item?.missingCostLineItems);
        existing.missingCostPounds += toNumber(item?.missingCostPounds);
      }

      byProduct.set(productId, existing);
    }

    return Array.from(byProduct.values())
      .map((row) => {
        const costOfGoodsSold = row.hasCompleteCostData ? Math.round(row.costOfGoodsSold * 100) / 100 : null;
        const grossIncome = row.hasCompleteCostData ? Math.round(row.grossIncome * 100) / 100 : null;
        return {
          ...row,
          totalBags: Math.round(row.totalBags * 100) / 100,
          totalPounds: Math.round(row.totalPounds * 100) / 100,
          totalRevenue: Math.round(row.totalRevenue * 100) / 100,
          costOfGoodsSold,
          grossIncome,
          grossMarginPercent: grossIncome != null && row.totalRevenue > 0
            ? Math.round((grossIncome / row.totalRevenue) * 10000) / 100
            : null,
          missingCostPounds: Math.round(row.missingCostPounds * 100) / 100,
        };
      })
      .sort((a, b) => {
        if (a.grossIncome != null && b.grossIncome != null) return b.grossIncome - a.grossIncome;
        if (a.grossIncome != null) return -1;
        if (b.grossIncome != null) return 1;
        return b.totalRevenue - a.totalRevenue;
      });
  }, [salesByProductRows]);

  const incompleteCostProductCount = React.useMemo(
    () => productPerformanceRows.filter((row) => !row.hasCompleteCostData).length,
    [productPerformanceRows],
  );

  const totalGrossIncome = React.useMemo(
    () => productPerformanceRows.reduce((sum, row) => sum + (row.grossIncome ?? 0), 0),
    [productPerformanceRows],
  );

  const avgPricePerLb = React.useMemo(
    () => (totalPoundsSold > 0 ? totalRevenue / totalPoundsSold : 0),
    [totalPoundsSold, totalRevenue],
  );

  const grossIncomeMetricValue = productPerformanceRows.length > 0 && incompleteCostProductCount === productPerformanceRows.length
    ? "Missing cost"
    : formatMoney(totalGrossIncome);
  const grossIncomeMetricHint = incompleteCostProductCount > 0
    ? `${incompleteCostProductCount.toLocaleString()} product(s) missing cost`
    : "Sales - COGS";

  const selectedClientName = React.useMemo(() => {
    if (selectedUserProfileId === "all-users") return "All clients";
    const found = usersRows.find((u) => u._id === selectedUserProfileId);
    return found?.company || found?.name || found?.email || "Selected client";
  }, [selectedUserProfileId, usersRows]);

  const contractVsSpotSummary = React.useMemo(
    () => ({
      contractRevenue: toNumber(contractVsSpot?.totals?.contract?.revenue),
      spotRevenue: toNumber(contractVsSpot?.totals?.spot?.revenue),
      contractValueImpact: toNumber(contractVsSpot?.totals?.totalContractValueImpact),
      priceDifference: toNumber(contractVsSpot?.summary?.overallPriceDifference),
      contractRevenuePct: toNumber(contractVsSpot?.summary?.contractPercentOfRevenue),
    }),
    [contractVsSpot],
  );

  const sampleImpactSummary = React.useMemo(
    () => ({
      totalSampleEvents: toNumber(sampleImpact?.summary?.totalSampleEvents),
      clientsSampled: toNumber(sampleImpact?.summary?.clientsSampled),
      anyPurchaseConversionRate: toNumber(sampleImpact?.summary?.anyPurchaseConversionRate),
      targetPurchaseConversionRate: toNumber(sampleImpact?.summary?.targetPurchaseConversionRate),
      avgOrderDelta: toNumber(sampleImpact?.summary?.orderDelta?.avgDelta),
      avgTargetOrderDelta: toNumber(sampleImpact?.summary?.targetOrderDelta?.avgDelta),
      avgDaysToFirstOrder: sampleImpact?.summary?.avgDaysToFirstOrder ?? null,
      avgDaysToTargetOrder: sampleImpact?.summary?.avgDaysToTargetOrder ?? null,
      noPurchaseAfterSample: toNumber(sampleImpact?.summary?.noPurchaseAfterSample),
    }),
    [sampleImpact],
  );

  const currentBoxRows = React.useMemo(() => {
    if (!boxSales) return [] as any[];
    if (boxSalesView === "summary") {
      return [
        {
          bucket: "Box sales",
          units: boxSales.summary?.boxSales?.units ?? 0,
          pounds: boxSales.summary?.boxSales?.pounds ?? 0,
          revenue: boxSales.summary?.boxSales?.revenue ?? 0,
          avgPricePerLb: boxSales.summary?.boxSales?.avgPricePerLb ?? 0,
        },
        {
          bucket: "Full bag sales",
          units: boxSales.summary?.fullBagSales?.units ?? 0,
          pounds: boxSales.summary?.fullBagSales?.pounds ?? 0,
          revenue: boxSales.summary?.fullBagSales?.revenue ?? 0,
          avgPricePerLb: boxSales.summary?.fullBagSales?.avgPricePerLb ?? 0,
        },
      ];
    }
    if (boxSalesView === "byClient") return (boxSales.byClient ?? []) as any[];
    if (boxSalesView === "byOrigin") return (boxSales.byOrigin ?? []) as any[];
    if (boxSalesView === "byProduct") return (boxSales.byProduct ?? []) as any[];
    return (boxSales.byCargo ?? []) as any[];
  }, [boxSales, boxSalesView]);

  const MAX_PIE_SLICES = 20;

  const cargoPieSlices = React.useMemo(() => {
    if (!cargoStatusDistribution?.slices) return { visible: [], all: [] };

    type SliceItem = { label: string; bagsSold: number; productName: string; revenue: number; contractBags: number; spotBags: number };
    const all = cargoStatusDistribution.slices as SliceItem[];

    if (all.length <= MAX_PIE_SLICES) {
      return {
        visible: all.map((item, idx) => ({ ...item, fill: PIE_PALETTE[idx % PIE_PALETTE.length] })),
        all,
      };
    }

    const top = all.slice(0, MAX_PIE_SLICES);
    const rest = all.slice(MAX_PIE_SLICES);
    const otherBags = rest.reduce((s, d) => s + d.bagsSold, 0);
    const otherRevenue = rest.reduce((s, d) => s + d.revenue, 0);
    const otherContract = rest.reduce((s, d) => s + d.contractBags, 0);
    const otherSpot = rest.reduce((s, d) => s + d.spotBags, 0);

    const combined = [
      ...top.map((item) => ({ ...item, isOther: false })),
      {
        label: `Other (${rest.length})`,
        bagsSold: otherBags,
        productName: `${rest.length} cargos`,
        revenue: otherRevenue,
        contractBags: otherContract,
        spotBags: otherSpot,
        isOther: true,
      },
    ];

    combined.sort((a, b) => b.bagsSold - a.bagsSold);

    const visible = combined.map((item, idx) => ({
      ...item,
      fill: item.isOther ? "hsl(0 0% 74%)" : PIE_PALETTE[idx % PIE_PALETTE.length],
    }));

    return { visible, all };
  }, [cargoStatusDistribution]);

  const cargoPieChartConfig = React.useMemo<ChartConfig>(() => {
    const config: ChartConfig = { bagsSold: { label: "Bags Sold" } };
    for (const item of cargoPieSlices.visible) {
      config[item.label] = { label: item.label, color: item.fill };
    }
    return config;
  }, [cargoPieSlices]);

  const clearFilters = React.useCallback(() => {
    updateSearchParams({
      clientId: null,
      company: null,
      productId: null,
      productName: null,
      cargoSearch: null,
      contractSearch: null,
      deliveredOnly: null,
      orderScope: null,
    });
  }, [updateSearchParams]);

  const revenueMixChartRows = React.useMemo(
    () =>
      revenueByTypeRows.map((row) => ({
        period: String(row.period),
        contractRevenue: toNumber(row.contractRevenue),
        spotRevenue: toNumber(row.spotRevenue),
      })),
    [revenueByTypeRows],
  );

  const exportSales = React.useCallback(() => {
    const rows = salesByProductRows.map((row) => ({
      date: row.date,
      productName: row.productName,
      totalBags: row.totalBags,
      totalPounds: row.totalPounds,
      totalRevenue: toNumber(row.totalRevenue),
      costOfGoodsSold: row.costOfGoodsSold,
      grossIncome: row.grossIncome,
      contractBags: row.contractBags,
      spotBags: row.spotBags,
    }));

    exportRows(
      rows,
      [
        { key: "date", label: "Date" },
        { key: "productName", label: "Product" },
        { key: "totalBags", label: "Units Sold" },
        { key: "totalPounds", label: "Lbs Sold" },
        { key: "totalRevenue", label: "Revenue", format: (v) => formatMoney(v) },
        { key: "costOfGoodsSold", label: "COGS", format: (v) => v == null ? "Missing cost" : formatMoney(v) },
        { key: "grossIncome", label: "Gross Income", format: (v) => v == null ? "Missing cost" : formatMoney(v) },
        { key: "contractBags", label: "Contract Units" },
        { key: "spotBags", label: "Spot Units" },
      ],
      `sales-by-product-${dateRange}.csv`,
    );
  }, [dateRange, salesByProductRows]);

  const exportProductGrossIncome = React.useCallback(() => {
    const rows = productPerformanceRows.map((row) => ({
      productName: row.productName,
      totalUnits: row.totalBags,
      totalPounds: row.totalPounds,
      totalRevenue: row.totalRevenue,
      costOfGoodsSold: row.costOfGoodsSold,
      grossIncome: row.grossIncome,
      grossMarginPercent: row.grossMarginPercent,
      missingCostPounds: row.missingCostPounds,
    }));

    exportRows(
      rows,
      [
        { key: "productName", label: "Product" },
        { key: "totalUnits", label: "Units Sold" },
        { key: "totalPounds", label: "Lbs Sold" },
        { key: "totalRevenue", label: "Sales", format: (v) => formatMoney(v) },
        { key: "costOfGoodsSold", label: "COGS", format: (v) => v == null ? "Missing cost" : formatMoney(v) },
        { key: "grossIncome", label: "Gross Income", format: (v) => v == null ? "Missing cost" : formatMoney(v) },
        { key: "grossMarginPercent", label: "Gross Margin %", format: (v) => v == null ? "N/A" : `${toNumber(v).toFixed(1)}%` },
        { key: "missingCostPounds", label: "Lbs Missing Cost" },
      ],
      `product-gross-income-${dateRange}.csv`,
    );
  }, [dateRange, productPerformanceRows]);

  const exportOrigins = React.useCallback(() => {
    const rows = salesByOriginRows.map((row) => ({
      originName: row.originName,
      totalBags: row.totalBags,
      totalRevenue: toNumber(row.totalRevenue),
      contractBags: row.contractBags,
      spotBags: row.spotBags,
      products: (row.productBreakdown ?? [])
        .map((p: any) =>
          [
            p.productName,
            formatSalesUnitLabel(p.unitType, p.unitsSold ?? p.bags),
            p.purchaseContractNumber || "-",
          ].join(" / "),
        )
        .join(" | "),
    }));

    exportRows(
      rows,
      [
        { key: "originName", label: "Origin" },
        { key: "totalBags", label: "Units Sold" },
        { key: "totalRevenue", label: "Revenue", format: (v) => formatMoney(v) },
        { key: "contractBags", label: "Contract Bags" },
        { key: "spotBags", label: "Spot Bags" },
        { key: "products", label: "Coffee Detail" },
      ],
      `sales-by-origin-${dateRange}.csv`,
    );
  }, [dateRange, salesByOriginRows]);

  const exportInventoryVelocity = React.useCallback(() => {
    const rows = inventoryVelocityRows.map((row) => ({
      productName: row.productName,
      ociNumber: row.ociNumber,
      currentStock: row.currentStock,
      salesPerDay: row.salesPerDay,
      salesPerWeek: row.salesPerWeek,
      salesPerMonth: row.salesPerMonth,
      daysUntilZero: row.daysUntilZero,
      predictedZeroDate: row.predictedZeroDate ? formatShortDate(row.predictedZeroDate) : "N/A",
      confidenceRange: `${row.confidence?.worstCaseDays ?? "-"} to ${row.confidence?.bestCaseDays ?? "-"}`,
    }));

    exportRows(
      rows,
      [
        { key: "productName", label: "Product" },
        { key: "ociNumber", label: "OCI" },
        { key: "currentStock", label: "Current Stock" },
        { key: "salesPerDay", label: "Sales/Day" },
        { key: "salesPerWeek", label: "Sales/Week" },
        { key: "salesPerMonth", label: "Sales/Month" },
        { key: "daysUntilZero", label: "Days Until Zero" },
        { key: "predictedZeroDate", label: "Predicted Zero Date" },
        { key: "confidenceRange", label: "Confidence Range" },
      ],
      `inventory-velocity-${dateRange}.csv`,
    );
  }, [dateRange, inventoryVelocityRows]);

  const exportPriceAnalytics = React.useCallback(() => {
    const rows = priceAnalyticsRows.map((row) => ({
      productName: row.productName,
      ociNumber: row.ociNumber,
      producerCost: row.producerCost ?? "N/A",
      avgSalePrice: row.avgSalePrice ?? "N/A",
      margin: row.margin ?? "N/A",
      totalVolume: row.totalVolume,
      totalPounds: row.totalPounds,
      totalRevenue: row.totalRevenue,
      hasCostData: row.hasCostData ? "Yes" : "No",
    }));

    exportRows(
      rows,
      [
        { key: "productName", label: "Product" },
        { key: "ociNumber", label: "OCI" },
        { key: "producerCost", label: "Producer Cost" },
        { key: "avgSalePrice", label: "Avg Sale Price" },
        { key: "margin", label: "Margin %" },
        { key: "totalVolume", label: "Volume" },
        { key: "totalPounds", label: "Total Pounds" },
        { key: "totalRevenue", label: "Revenue" },
        { key: "hasCostData", label: "Has Cost Data" },
      ],
      `price-analytics-${dateRange}.csv`,
    );
  }, [dateRange, priceAnalyticsRows]);

  const exportInventoryAge = React.useCallback(() => {
    const rows = inventoryAgeRows.map((row) => ({
      ociNumber: row.ociNumber,
      productName: row.productName,
      originName: row.originName,
      warehouseName: row.warehouseName,
      daysInWarehouse: row.daysInWarehouse,
      availableBags: row.inventory?.availableBags ?? 0,
      percentSold: row.inventory?.percentSold ?? 0,
      lastSoldDate: row.lastSoldDateFormatted || "Never",
      daysSinceLastSale: row.daysSinceLastSale ?? "N/A",
      priceDiff: row.pricing?.diff ?? "N/A",
      outlier: row.isOutlier ? "Yes" : "No",
    }));

    exportRows(
      rows,
      [
        { key: "ociNumber", label: "OCI" },
        { key: "productName", label: "Product" },
        { key: "originName", label: "Origin" },
        { key: "warehouseName", label: "Warehouse" },
        { key: "daysInWarehouse", label: "Days in Warehouse" },
        { key: "availableBags", label: "Available Bags" },
        { key: "percentSold", label: "% Sold" },
        { key: "lastSoldDate", label: "Last Sold Date" },
        { key: "daysSinceLastSale", label: "Days Since Last Sale" },
        { key: "priceDiff", label: "Price Diff" },
        { key: "outlier", label: "Outlier" },
      ],
      `inventory-age-${dateRange}.csv`,
    );
  }, [dateRange, inventoryAgeRows]);

  const exportBoxSales = React.useCallback(() => {
    let rows: Record<string, unknown>[] = [];

    if (boxSalesView === "summary") {
      rows = currentBoxRows.map((row: any) => ({
        bucket: row.bucket,
        units: row.units,
        pounds: row.pounds,
        revenue: row.revenue,
        avgPricePerLb: row.avgPricePerLb,
      }));
    }

    if (boxSalesView === "byClient") {
      rows = currentBoxRows.map((row: any) => ({
        clientName: row.clientName,
        company: row.company,
        enterprise: getEnterpriseBadgeLabel(row.isEnterprise),
        units: row.metrics?.units ?? 0,
        pounds: row.metrics?.pounds ?? 0,
        revenue: row.metrics?.revenue ?? 0,
        avgPricePerLb: row.metrics?.avgPricePerLb ?? 0,
      }));
    }

    if (boxSalesView === "byOrigin") {
      rows = currentBoxRows.map((row: any) => ({
        originName: row.originName,
        units: row.metrics?.units ?? 0,
        pounds: row.metrics?.pounds ?? 0,
        revenue: row.metrics?.revenue ?? 0,
        avgPricePerLb: row.metrics?.avgPricePerLb ?? 0,
      }));
    }

    if (boxSalesView === "byProduct") {
      rows = currentBoxRows.map((row: any) => ({
        productName: row.productName,
        originName: row.originName,
        processType: row.processType || "N/A",
        units: row.metrics?.units ?? 0,
        pounds: row.metrics?.pounds ?? 0,
        revenue: row.metrics?.revenue ?? 0,
        avgPricePerLb: row.metrics?.avgPricePerLb ?? 0,
      }));
    }

    if (boxSalesView === "byCargo") {
      rows = currentBoxRows.map((row: any) => ({
        ociNumber: row.ociNumber,
        productName: row.productName,
        originName: row.originName,
        processType: row.processType || "N/A",
        units: row.metrics?.units ?? 0,
        pounds: row.metrics?.pounds ?? 0,
        revenue: row.metrics?.revenue ?? 0,
        avgPricePerLb: row.metrics?.avgPricePerLb ?? 0,
      }));
    }

    const firstRow = rows[0];
    const keys = firstRow ? Object.keys(firstRow) : [];
    const columns = keys.map((key) => ({
      key,
      label: key,
      format: key === "revenue" || key === "avgPricePerLb" ? (v: unknown) => formatMoney(v) : undefined,
    })) as ExportColumn[];

    exportRows(rows, columns, `box-sales-${boxSalesView}-${dateRange}.csv`);
  }, [boxSalesView, currentBoxRows, dateRange]);

  const exportContractVsSpotByCargo = React.useCallback(() => {
    const rows = contractVsSpotByCargoRows.map((row) => ({
      ociNumber: row.ociNumber,
      productName: row.productName,
      contractBags: row.contract?.bags ?? 0,
      contractAvgPrice: row.contract?.avgPricePerLb ?? 0,
      spotBags: row.spot?.bags ?? 0,
      spotAvgPrice: row.spot?.avgPricePerLb ?? 0,
      diffPerLb: row.comparison?.priceDifferencePerLb ?? 0,
      interpretation: row.comparison?.interpretation,
      hasBothTypes: row.comparison?.hasBothTypes ? "Yes" : "No",
    }));

    exportRows(
      rows,
      [
        { key: "ociNumber", label: "OCI" },
        { key: "productName", label: "Product" },
        { key: "contractBags", label: "Contract Bags" },
        { key: "contractAvgPrice", label: "Contract Avg $/lb", format: (v) => formatMoney(v) },
        { key: "spotBags", label: "Spot Bags" },
        { key: "spotAvgPrice", label: "Spot Avg $/lb", format: (v) => formatMoney(v) },
        { key: "diffPerLb", label: "Diff $/lb", format: (v) => formatMoney(v) },
        { key: "interpretation", label: "Interpretation" },
        { key: "hasBothTypes", label: "Comparable" },
      ],
      `contract-vs-spot-by-cargo-${dateRange}.csv`,
    );
  }, [contractVsSpotByCargoRows, dateRange]);

  const exportClientOrders = React.useCallback(() => {
    const rows = clientOrderRows.map((row) => ({
      orderNumber: row.orderNumber,
      customer: row.user?.company || row.user?.name || row.user?.email || "Unknown",
      enterprise: getEnterpriseBadgeLabel(row.isEnterprise),
      status: formatStatusLabel(row.status),
      totalAmount: row.totalAmount,
      pickupDate: row.delivery?.pickupDate ? formatShortDate(row.delivery.pickupDate) : "N/A",
      createdAt: formatShortDate(row.createdAt || row._creationTime),
    }));

    exportRows(
      rows,
      [
        { key: "orderNumber", label: "Order #" },
        { key: "customer", label: "Customer" },
        { key: "enterprise", label: "Enterprise" },
        { key: "status", label: "Status" },
        { key: "totalAmount", label: "Total" },
        { key: "pickupDate", label: "Pickup Date" },
        { key: "createdAt", label: "Created" },
      ],
      `orders-${selectedClientName.toLowerCase().replace(/\s+/g, "-")}-${dateRange}.csv`,
    );
  }, [clientOrderRows, dateRange, selectedClientName]);

  const exportSampleImpact = React.useCallback(() => {
    const rows = sampleImpactRows.map((row) => ({
      sampleDate: formatShortDate(row.sampleEventAt),
      client: row.clientName,
      company: row.clientCompany || "",
      enterprise: getEnterpriseBadgeLabel(row.clientIsEnterprise),
      targetType: row.targetType,
      targetProduct: row.targetProductName || "",
      targetCargo: row.targetCargoOci || "",
      conversion: row.conversionOutcome,
      preOrders: row.preWindowOrders,
      postOrders: row.postWindowOrders,
      orderDelta: row.orderDelta,
      preTargetOrders: row.preWindowTargetOrders,
      postTargetOrders: row.postWindowTargetOrders,
      targetOrderDelta: row.targetOrderDelta,
      preRevenue: row.preWindowRevenue,
      postRevenue: row.postWindowRevenue,
      revenueDelta: row.revenueDelta,
      firstOrderAfterDays: row.firstOrderAfterDays ?? "",
      firstTargetOrderAfterDays: row.firstTargetOrderAfterDays ?? "",
      topProductsAfter: (row.postWindowTopProducts ?? []).map((p: any) => p.productName).join(" | "),
    }));

    exportRows(
      rows,
      [
        { key: "sampleDate", label: "Sample Date" },
        { key: "client", label: "Client" },
        { key: "company", label: "Company" },
        { key: "enterprise", label: "Enterprise" },
        { key: "targetType", label: "Target Type" },
        { key: "targetProduct", label: "Target Product" },
        { key: "targetCargo", label: "Target Cargo" },
        { key: "conversion", label: "Conversion" },
        { key: "preOrders", label: "Pre Orders" },
        { key: "postOrders", label: "Post Orders" },
        { key: "orderDelta", label: "Order Delta" },
        { key: "preTargetOrders", label: "Pre Target Orders" },
        { key: "postTargetOrders", label: "Post Target Orders" },
        { key: "targetOrderDelta", label: "Target Order Delta" },
        { key: "preRevenue", label: "Pre Revenue", format: (v) => formatMoney(v) },
        { key: "postRevenue", label: "Post Revenue", format: (v) => formatMoney(v) },
        { key: "revenueDelta", label: "Revenue Delta", format: (v) => formatMoney(v) },
        { key: "firstOrderAfterDays", label: "Days to First Order" },
        { key: "firstTargetOrderAfterDays", label: "Days to First Target Order" },
        { key: "topProductsAfter", label: "Top Products After" },
      ],
      `sample-impact-${dateRange}.csv`,
    );
  }, [dateRange, sampleImpactRows]);

  return (
    <div className="space-y-8 [&_[data-slot=card]]:shadow-none">
      <section className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Sales Analytics
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">Data-driven insights</h1>
            <p className="max-w-2xl text-xs text-muted-foreground">
              {isSalesAudience
                ? "Track sales performance, inventory velocity, and pricing trends for your assigned clients."
                : "Track sales performance, inventory velocity, and pricing trends across products and origins."}
            </p>
          </div>
        </div>

        {!hasAnalyticsAccess ? (
          <Card className="border-amber-300 bg-amber-50/60 shadow-none dark:border-amber-800/60 dark:bg-amber-950/25">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {isSalesAudience ? "Sales access required" : "Admin required"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {isSalesAudience
                  ? "This page is available to sales, marketing, and admin users."
                  : "This page is restricted to admin users."}
              </p>
            </CardContent>
          </Card>
        ) : null}

        <div className={FILTER_CARD_CLASS}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Time Range:</span>
              <ComboboxSelect
                className="w-[220px]"
                value={dateRange}
                options={DATE_RANGE_OPTIONS}
                placeholder="Select time range"
                searchPlaceholder="Search time range..."
                emptyText="No time ranges found."
                onValueChange={handleDateRangeFilterChange}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Show:</span>
              <Select value={orderScope} onValueChange={setOrderScope}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_SCOPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Client:</span>
              {isLoadingUsers ? (
                <Skeleton className="h-9 w-[250px]" />
              ) : (
                <ComboboxSelect
                  className="w-[280px]"
                  value={selectedUserProfileId}
                  options={userFilterOptions}
                  placeholder="Select client"
                  searchPlaceholder="Search clients..."
                  emptyText="No clients found."
                  onValueChange={handleClientFilterChange}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Product:</span>
              {isLoadingProducts ? (
                <Skeleton className="h-9 w-[220px]" />
              ) : (
                <ComboboxSelect
                  className="w-[260px]"
                  value={selectedProductId}
                  options={productFilterOptions}
                  placeholder="Select product"
                  searchPlaceholder="Search products..."
                  emptyText="No products found."
                  onValueChange={handleProductFilterChange}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Cargo / OCI:</span>
              {isLoadingCargoFilters ? (
                <Skeleton className="h-9 w-[220px]" />
              ) : (
                <ComboboxSelect
                  className="w-[220px]"
                  value={cargoSearch || "all-cargos"}
                  options={cargoOciFilterOptions}
                  placeholder="Select cargo / OCI"
                  searchPlaceholder="Search cargos..."
                  emptyText="No cargos found."
                  onValueChange={setCargoSearch}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Contract / PO:</span>
              {isLoadingCargoFilters ? (
                <Skeleton className="h-9 w-[220px]" />
              ) : (
                <ComboboxSelect
                  className="w-[220px]"
                  value={contractSearch || "all-contracts"}
                  options={contractFilterOptions}
                  placeholder="Select contract / PO"
                  searchPlaceholder="Search contracts..."
                  emptyText="No contracts found."
                  onValueChange={setContractSearch}
                />
              )}
            </div>

            <label htmlFor="deliveredOnly" className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
              <Checkbox
                id="deliveredOnly"
                checked={deliveredOnly}
                onCheckedChange={(checked) => setDeliveredOnly(Boolean(checked))}
              />
              Delivered only
            </label>

            {(selectedUserProfileId !== "all-users" ||
              selectedProductId !== "all-products" ||
              cargoSearch ||
              contractSearch ||
              deliveredOnly ||
              orderScope !== "orders") ? (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
          </div>

          {isOverviewTab && hasAnalyticsAccess ? (
            <div className="mt-4 border-t pt-4">
              <ClientReportButton
                enabled={canUseAnalyticsQueries && !isLoadingUsers}
                orderScope={orderScope}
                filters={{
                  userId: selectedUserProfileId === "all-users" ? undefined : selectedUserProfileId,
                  dateRange,
                  productId: selectedProductId === "all-products" ? undefined : selectedProductId,
                  deliveredOnly,
                  cargoSearch: cargoSearch || undefined,
                  contractSearch: contractSearch || undefined,
                }}
              />
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Contract view:</span>
              <Select
                value={contractGroupBy}
                onValueChange={(value) => setContractGroupBy(value as ContractGroupBy)}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_GROUP_BY.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Box view:</span>
              <Select value={boxSalesView} onValueChange={(value) => setBoxSalesView(value as BoxSalesView)}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BOX_SALES_VIEWS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Age threshold:</span>
              <Input
                type="number"
                min={1}
                className="h-9 w-[120px]"
                value={inventoryAgeThresholdInput}
                onChange={(event) => setInventoryAgeThresholdInput(event.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Sample lookback:</span>
              <Input
                type="number"
                min={1}
                className="h-9 w-[110px]"
                value={sampleLookbackDaysInput}
                onChange={(event) => setSampleLookbackDaysInput(event.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Sample lookforward:</span>
              <Input
                type="number"
                min={1}
                className="h-9 w-[110px]"
                value={sampleLookforwardDaysInput}
                onChange={(event) => setSampleLookforwardDaysInput(event.target.value)}
              />
            </div>
          </div>

          {isPricingTab ? (
            isLoadingDataQuality ? (
              <Skeleton className="mt-4 h-4 w-96 max-w-full" />
            ) : dataQuality?.orderStats ? (
              <p className="mt-4 text-xs text-muted-foreground">
                Analyzing {toNumber(dataQuality.orderStats.validForAnalytics).toLocaleString()} orders
                (excluded: {toNumber(dataQuality.orderStats.testOrders)} test, {toNumber(dataQuality.orderStats.cancelledOrders)} cancelled)
              </p>
            ) : null
          ) : null}
        </div>
      </section>

      <Tabs value={analyticsTab} onValueChange={(value) => setAnalyticsTab(value as AnalyticsTab)}>
        <TabsList>
          {availableAnalyticsTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {analyticsTab === "pricing" && dataQuality?.missingCostData ? (
        <Card className="border-red-300 bg-red-50/60 shadow-none dark:border-red-800/60 dark:bg-red-950/25">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Data Quality</CardTitle>
            <CardDescription>
              {toNumber(dataQuality.missingCostData)} of {toNumber(dataQuality.activeCargos)} active cargos are missing producer cost data.
            </CardDescription>
          </CardHeader>
          {Array.isArray(dataQuality.missingCostSample) && dataQuality.missingCostSample.length > 0 ? (
            <CardContent>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {dataQuality.missingCostSample.map((sample: any) => (
                  <li key={sample.id}>
                    OCI {sample.ociNumber || "N/A"} ({formatStatusLabel(sample.status)})
                  </li>
                ))}
              </ul>
            </CardContent>
          ) : null}
        </Card>
      ) : null}

      {analyticsTab === "overview" ? (
        <>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title={orderStatsCountTitle}
          value={formatMetricNumber(orderFilterStats?.totalOrders, 0)}
          hint={orderStatsScopeHint}
          isLoading={isLoadingOrderFilterStats}
        />
        <MetricCard
          title={orderStatsAmountTitle}
          value={formatMoney(orderFilterStats?.totalOrderAmount)}
          hint="Final order totals"
          isLoading={isLoadingOrderFilterStats}
        />
        <MetricCard
          title="Avg Order Value"
          value={formatMoney(orderFilterStats?.avgOrderValue)}
          hint="Order amount / orders"
          isLoading={isLoadingOrderFilterStats}
        />
        <MetricCard
          title="Avg Order Size"
          value={formatUnits(orderFilterStats?.avgUnitsPerOrder)}
          hint="Matched bags/boxes per order"
          isLoading={isLoadingOrderFilterStats}
        />
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          title="Units Sold"
          value={formatMetricNumber(totalBagsSold, 0)}
          hint="Bags/boxes across selected scope"
          isLoading={isLoadingSalesByProduct}
        />
        <MetricCard
          title="Lbs Sold"
          value={formatMetricNumber(totalPoundsSold, 0)}
          hint="Weight sold across selected scope"
          isLoading={isLoadingSalesByProduct}
        />
        <MetricCard
          title="Total Revenue"
          value={formatMoney(totalRevenue)}
          hint="Sales by product"
          isLoading={isLoadingSalesByProduct}
        />
        <MetricCard
          title="Gross Income"
          value={grossIncomeMetricValue}
          hint={grossIncomeMetricHint}
          isLoading={isLoadingSalesByProduct}
        />
        <MetricCard
          title="Avg Price / lb"
          value={formatMoney(avgPricePerLb)}
          hint="Revenue / pounds sold"
          isLoading={isLoadingSalesByProduct}
        />
      </section>

      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Sales Over Time</CardTitle>
              <CardDescription>Weekly unit volume for selected range.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportSales} disabled={salesByProductRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingSalesByProduct ? (
            <Skeleton className="h-72 w-full" />
          ) : salesChartRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No sales data in this range.</p>
          ) : (
            <ChartContainer config={SALES_CHART_CONFIG} className="aspect-auto h-72 w-full">
              <BarChart data={salesChartRows} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={16}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Bar
                  dataKey="bags"
                  fill="var(--color-bags)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Product Gross Income</CardTitle>
              <CardDescription>Products ranked by sales revenue minus cost of goods sold.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportProductGrossIncome} disabled={productPerformanceRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isLoadingSalesByProduct && incompleteCostProductCount > 0 ? (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
              {incompleteCostProductCount.toLocaleString()} product(s) have sales tied to cargos without cost data, so gross income is hidden for those products.
            </div>
          ) : null}

          {isLoadingSalesByProduct ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : productPerformanceRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No product gross income data in this range.</p>
          ) : (
            <InternalEntityDataTable
              data={productPerformanceRows}
              columns={productGrossIncomeColumns}
              pageSize={10}
              initialSorting={[{ id: "grossIncome", desc: true }]}
              enableSelection={false}
            />
          )}
        </CardContent>
      </Card>

      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Sales by Origin</CardTitle>
              <CardDescription>Origin-level performance and product mix.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setComparisonOpen(true)} disabled={selectedUserProfileId === "all-users"}>
                Compare for campaign
              </Button>
              <Button variant="outline" size="sm" onClick={exportOrigins} disabled={salesByOriginRows.length === 0}>
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {comparisonOpen && selectedUserProfileId !== "all-users" ? (
            <ContractComparisonBuilder key={selectedUserProfileId} customerId={selectedUserProfileId} initialCampaignId={comparisonCampaignId} onClose={() => {
              setComparisonOpen(false);
              updateSearchParams({ comparisonCampaignId: null });
            }} />
          ) : isLoadingSalesByOrigin ? (
            <SectionTableSkeleton rows={8} cols={6} />
          ) : salesByOriginRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No origin data in this range.</p>
          ) : (
            <InternalEntityDataTable
              data={salesByOriginRows}
              columns={salesByOriginColumns}
              pageSize={8}
              initialSorting={[{ id: "totalRevenue", desc: true }]}
              enableSelection={false}
              expandedRow={{
                render: SalesByOriginExpandedRow as React.ComponentType<{ row: any }>,
                getCanExpand: (row) => (row.productBreakdown?.length ?? 0) > 0,
              }}
            />
          )}
          {selectedUserProfileId === "all-users" && (
            <p className="mt-3 text-xs text-muted-foreground">Select a client above to build a contract comparison from their purchase history.</p>
          )}
        </CardContent>
      </Card>

      {audience === "admin" ? (
        <OrdersTab authUserId={authUserId} orderScope={orderScope} />
      ) : (
        <SalesOrdersTable
          showPageHeader={false}
          scopeToSearchParams
          orderScope={orderScope}
        />
      )}

        </>
      ) : null}

      {analyticsTab === "inventory" ? (
      <>
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            title="Bags Sold"
            value={cargoStatusDistribution?.totalBagsSold?.toLocaleString() ?? "0"}
            hint={DATE_RANGES.find((r) => r.value === cargoPieDateRange)?.label ?? "All time"}
            isLoading={isLoadingCargoStatus}
          />
          <MetricCard
            title="Active Cargos"
            value={cargoStatusDistribution?.totalCargos?.toLocaleString() ?? "0"}
            hint="With sales in period"
            isLoading={isLoadingCargoStatus}
          />
          <MetricCard
            title="Top Cargo"
            value={cargoPieSlices.visible[0]?.label ?? "-"}
            hint={cargoPieSlices.visible[0] ? `${cargoPieSlices.visible[0].bagsSold.toLocaleString()} bags` : "No data"}
            isLoading={isLoadingCargoStatus}
          />
          <MetricCard
            title="Revenue"
            value={formatMoney(cargoStatusDistribution?.totalRevenue)}
            hint="Total in period"
            isLoading={isLoadingCargoStatus}
          />
        </section>

        <Card className={PANEL_CARD_CLASS}>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>Bags Sold by Cargo</CardTitle>
                <CardDescription>Volume distribution by contract number</CardDescription>
              </div>
              <Select value={cargoPieDateRange} onValueChange={(v) => setCargoPieDateRange(v as DateRange)}>
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value} className="text-xs">
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingCargoStatus ? (
              <div className="grid items-center gap-6 lg:grid-cols-[1fr_300px]">
                <div className="mx-auto flex aspect-square w-full max-w-[320px] items-center justify-center">
                  <Skeleton className="h-[260px] w-[260px] rounded-full" />
                </div>
                <div className="space-y-4 py-2">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-3 w-3 shrink-0 rounded-sm" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-3.5 w-24" />
                        <Skeleton className="h-3 w-36" />
                      </div>
                      <Skeleton className="h-4 w-10" />
                    </div>
                  ))}
                </div>
              </div>
            ) : cargoPieSlices.visible.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">No sales data in the selected range.</p>
            ) : (
              <div className="grid items-start gap-6 lg:grid-cols-[1fr_300px]">
                <ChartContainer
                  config={cargoPieChartConfig}
                  className="mx-auto aspect-square w-full max-w-[320px]"
                >
                  <PieChart>
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          hideLabel
                          formatter={(value, name) => (
                            <span className="text-xs">
                              {String(name)}: <span className="font-semibold">{toNumber(value).toLocaleString()}</span> bags
                            </span>
                          )}
                        />
                      }
                    />
                    <Pie
                      data={cargoPieSlices.visible}
                      dataKey="bagsSold"
                      nameKey="label"
                      innerRadius="55%"
                      outerRadius="85%"
                      strokeWidth={2}
                      stroke="hsl(var(--background))"
                    >
                      {cargoPieSlices.visible.map((entry) => (
                        <Cell key={entry.label} fill={entry.fill} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                            return (
                              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                <tspan x={viewBox.cx} y={(viewBox.cy ?? 0) - 8} className="fill-foreground text-2xl font-bold">
                                  {cargoStatusDistribution?.totalBagsSold?.toLocaleString() ?? "0"}
                                </tspan>
                                <tspan x={viewBox.cx} y={(viewBox.cy ?? 0) + 12} className="fill-muted-foreground text-xs">
                                  bags sold
                                </tspan>
                              </text>
                            );
                          }
                          return null;
                        }}
                      />
                    </Pie>
                  </PieChart>
                </ChartContainer>

                <div className="max-h-[400px] overflow-y-auto">
                  <div className="flex flex-col gap-2 pr-2 text-sm">
                    {cargoPieSlices.visible.map((item) => {
                      const pct = cargoStatusDistribution?.totalBagsSold
                        ? Math.round((item.bagsSold / cargoStatusDistribution.totalBagsSold) * 100)
                        : 0;
                      return (
                        <div key={item.label} className="flex items-center gap-3">
                          <div className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: item.fill }} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium leading-tight">{item.label}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.productName}</p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="tabular-nums font-medium leading-tight">{item.bagsSold.toLocaleString()}</p>
                            <p className="text-xs tabular-nums text-muted-foreground">{pct}%</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </>
      ) : null}

      {analyticsTab === "inventory" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Inventory Velocity</CardTitle>
              <CardDescription>Stockout prediction from real sales velocity.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportInventoryVelocity} disabled={inventoryVelocityRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isLoadingInventoryVelocity && inventoryVelocityRows.filter((item) => toNumber(item.daysUntilZero) < 30).length > 0 ? (
            <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-300">
              {inventoryVelocityRows.filter((item) => toNumber(item.daysUntilZero) < 30).length} cargo(s) may stock out in under 30 days.
            </div>
          ) : null}

          {isLoadingInventoryVelocity ? (
            <SectionTableSkeleton rows={8} cols={6} />
          ) : inventoryVelocityRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No inventory velocity data available.</p>
          ) : (
            <div className="max-h-[360px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    <TableHead className="py-2 pr-4 font-medium">Product</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">OCI</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Stock</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Sales/Day</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Days to Zero</TableHead>
                    <TableHead className="py-2 font-medium">Predicted Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventoryVelocityRows.map((row) => (
                    <TableRow key={row.cargoId} className="border-b last:border-0">
                      <TableCell className="py-2 pr-4">{row.productName}</TableCell>
                      <TableCell className="py-2 pr-4 font-mono text-xs">{row.ociNumber}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.currentStock).toLocaleString()}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.salesPerDay).toFixed(2)}</TableCell>
                      <TableCell className="py-2 pr-4">
                        <Badge variant="outline" className={getVelocityBadgeClass(toNumber(row.daysUntilZero))}>
                          {toNumber(row.daysUntilZero).toLocaleString()}d
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground">
                        {row.predictedZeroDate ? formatShortDate(row.predictedZeroDate) : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "pricing" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Price Analytics</CardTitle>
              <CardDescription>Producer cost, sale price, and margin by cargo.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportPriceAnalytics} disabled={priceAnalyticsRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isLoadingPriceAnalytics && priceAnalyticsRows.filter((row) => !row.hasCostData).length > 0 ? (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
              {priceAnalyticsRows.filter((row) => !row.hasCostData).length} cargo(s) are missing producer cost data.
            </div>
          ) : null}

          {isLoadingPriceAnalytics ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : priceAnalyticsRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No price analytics in this range.</p>
          ) : (
            <div className="max-h-[360px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    <TableHead className="py-2 pr-4 font-medium">Product</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">OCI</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Producer Cost</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Avg Sale</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Margin %</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Volume</TableHead>
                    <TableHead className="py-2 font-medium">Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceAnalyticsRows.map((row) => (
                    <TableRow key={row.cargoId} className="border-b last:border-0">
                      <TableCell className="py-2 pr-4">{row.productName}</TableCell>
                      <TableCell className="py-2 pr-4 font-mono text-xs">{row.ociNumber}</TableCell>
                      <TableCell className="py-2 pr-4 text-xs">{String(row.producerCost ?? "N/A")}</TableCell>
                      <TableCell className="py-2 pr-4 text-xs">{String(row.avgSalePrice ?? "N/A")}</TableCell>
                      <TableCell className="py-2 pr-4">{row.margin == null ? "N/A" : `${toNumber(row.margin).toFixed(1)}%`}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.totalVolume).toLocaleString()}</TableCell>
                      <TableCell className="py-2">{String(row.totalRevenue ?? "-")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "inventory" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Inventory Age Analysis</CardTitle>
              <CardDescription>
                Aging threshold: {inventoryAgeThreshold} days.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportInventoryAge} disabled={inventoryAgeRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Tracked Cargos"
              value={toNumber(inventoryAge?.summary?.totalCargos).toLocaleString()}
              hint="With available inventory"
              isLoading={isLoadingInventoryAge}
            />
            <MetricCard
              title="Outliers"
              value={toNumber(inventoryAge?.summary?.outlierCount).toLocaleString()}
              hint={`Above ${inventoryAgeThreshold} days`}
              isLoading={isLoadingInventoryAge}
            />
            <MetricCard
              title="Avg Days"
              value={toNumber(inventoryAge?.summary?.avgDaysInWarehouse).toLocaleString()}
              hint="Average days in warehouse"
              isLoading={isLoadingInventoryAge}
            />
            <MetricCard
              title="Value at Risk"
              value={formatMoney(inventoryAge?.summary?.outlierValue)}
              hint="Outlier inventory value"
              isLoading={isLoadingInventoryAge}
            />
          </section>

          {!isLoadingInventoryAge && Array.isArray(inventoryAge?.ageDistribution) && inventoryAge.ageDistribution.length > 0 ? (
            <div className="space-y-2">
              {inventoryAge.ageDistribution.map((range: any) => {
                const maxCount = Math.max(...(inventoryAge.ageDistribution as any[]).map((r: any) => toNumber(r.count)), 1);
                const width = (toNumber(range.count) / maxCount) * 100;
                return (
                  <div key={range.label} className="grid grid-cols-[120px_1fr_64px] items-center gap-3 text-xs">
                    <span className="text-muted-foreground">{range.label}</span>
                    <div className="h-2 overflow-hidden rounded bg-muted">
                      <div className="h-full rounded bg-sky-600" style={{ width: `${width}%` }} />
                    </div>
                    <span className="text-right font-medium">{toNumber(range.count)}</span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {isLoadingInventoryAge ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : inventoryAgeRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No inventory age data available.</p>
          ) : (
            <div className="max-h-[360px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    <TableHead className="py-2 pr-4 font-medium">OCI</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Product</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Origin</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Days</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Available Bags</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Last Sold</TableHead>
                    <TableHead className="py-2 font-medium">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventoryAgeRows.slice(0, 200).map((row) => (
                    <TableRow key={row.cargoId} className="border-b last:border-0">
                      <TableCell className="py-2 pr-4 font-mono text-xs">{row.ociNumber}</TableCell>
                      <TableCell className="py-2 pr-4">{row.productName}</TableCell>
                      <TableCell className="py-2 pr-4">{row.originName}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.daysInWarehouse).toLocaleString()}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.inventory?.availableBags).toLocaleString()}</TableCell>
                      <TableCell className="py-2 pr-4 text-xs">{row.lastSoldDateFormatted || "Never"}</TableCell>
                      <TableCell className="py-2">
                        <Badge variant={row.isOutlier ? "destructive" : "secondary"}>
                          {row.isOutlier ? "Outlier" : "Normal"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "pricing" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <CardTitle>Contract vs Spot Analysis</CardTitle>
          <CardDescription>Grouped by {formatStatusLabel(contractGroupBy)}.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title="Groups"
              value={contractVsSpotRows.length.toLocaleString()}
              hint="Buckets in view"
              isLoading={isLoadingContractVsSpot}
            />
            <MetricCard
              title="Contract Revenue"
              value={formatMoney(contractVsSpotSummary.contractRevenue)}
              hint="Contract orders"
              isLoading={isLoadingContractVsSpot}
            />
            <MetricCard
              title="Spot Revenue"
              value={formatMoney(contractVsSpotSummary.spotRevenue)}
              hint="Spot orders"
              isLoading={isLoadingContractVsSpot}
            />
            <MetricCard
              title="Diff / lb"
              value={formatMoney(contractVsSpotSummary.priceDifference)}
              hint="Spot - contract"
              isLoading={isLoadingContractVsSpot}
            />
            <MetricCard
              title="Contract % Rev"
              value={`${contractVsSpotSummary.contractRevenuePct.toFixed(0)}%`}
              hint="Revenue share"
              isLoading={isLoadingContractVsSpot}
            />
          </section>

          {isLoadingContractVsSpot ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : contractVsSpotRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No contract-vs-spot data in this range.</p>
          ) : (
            <div className="max-h-[360px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    <TableHead className="py-2 pr-4 font-medium">Group</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Contract Bags</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Contract Rev</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Spot Bags</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Spot Rev</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Diff / lb</TableHead>
                    <TableHead className="py-2 font-medium">Interpretation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractVsSpotRows.map((row) => (
                    <TableRow key={row.key} className="border-b last:border-0">
                      <TableCell className="py-2 pr-4">{row.label}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.contract?.bags).toLocaleString()}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.contract?.revenue)}</TableCell>
                      <TableCell className="py-2 pr-4">{toNumber(row.spot?.bags).toLocaleString()}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.spot?.revenue)}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.comparison?.priceDifferencePerLb)}</TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground">{row.comparison?.interpretation || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "pricing" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <CardTitle>Revenue Mix: Contract vs Spot</CardTitle>
          <CardDescription>Revenue split by period.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingRevenueMix ? (
            <Skeleton className="h-72 w-full" />
          ) : revenueByTypeRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No revenue mix data available.</p>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                Contract: {formatMoney(revenueByPricingType?.totals?.contractRevenue)} ({toNumber(revenueByPricingType?.totals?.contractPercentage)}%)
                {" · "}
                Spot: {formatMoney(revenueByPricingType?.totals?.spotRevenue)} ({100 - toNumber(revenueByPricingType?.totals?.contractPercentage)}%)
              </div>

              <ChartContainer config={REVENUE_MIX_CHART_CONFIG} className="aspect-auto h-72 w-full">
                <BarChart data={revenueMixChartRows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="period"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        formatter={(value, name) => (
                          <span className="font-mono text-xs">
                            {name}: {formatMoney(value)}
                          </span>
                        )}
                      />
                    }
                  />
                  <Bar
                    dataKey="contractRevenue"
                    stackId="revenue"
                    fill="var(--color-contractRevenue)"
                    radius={[0, 0, 6, 6]}
                  />
                  <Bar
                    dataKey="spotRevenue"
                    stackId="revenue"
                    fill="var(--color-spotRevenue)"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "inventory" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Box Sales Analysis</CardTitle>
              <CardDescription>View: {BOX_SALES_VIEWS.find((v) => v.value === boxSalesView)?.label || boxSalesView}</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportBoxSales} disabled={currentBoxRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Box Units"
              value={toNumber(boxSales?.summary?.boxSales?.units).toLocaleString()}
              hint="Units under threshold"
              isLoading={isLoadingBoxSales}
            />
            <MetricCard
              title="Box Revenue"
              value={formatMoney(boxSales?.summary?.boxSales?.revenue)}
              hint="Revenue from box orders"
              isLoading={isLoadingBoxSales}
            />
            <MetricCard
              title="Box % Revenue"
              value={`${toNumber(boxSales?.summary?.boxPercentOfRevenue)}%`}
              hint="Revenue mix"
              isLoading={isLoadingBoxSales}
            />
            <MetricCard
              title="Box % Pounds"
              value={`${toNumber(boxSales?.summary?.boxPercentOfPounds)}%`}
              hint="Weight mix"
              isLoading={isLoadingBoxSales}
            />
          </section>

          {isLoadingBoxSales ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : currentBoxRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No box sales data available.</p>
          ) : (
            <div className="max-h-[360px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    {boxSalesView === "summary" ? <TableHead className="py-2 pr-4 font-medium">Bucket</TableHead> : null}
                    {boxSalesView === "byClient" ? <TableHead className="py-2 pr-4 font-medium">Client</TableHead> : null}
                    {boxSalesView === "byClient" ? <TableHead className="py-2 pr-4 font-medium">Enterprise</TableHead> : null}
                    {boxSalesView === "byOrigin" ? <TableHead className="py-2 pr-4 font-medium">Origin</TableHead> : null}
                    {boxSalesView === "byProduct" ? <TableHead className="py-2 pr-4 font-medium">Product</TableHead> : null}
                    {boxSalesView === "byCargo" ? <TableHead className="py-2 pr-4 font-medium">OCI</TableHead> : null}

                    {boxSalesView !== "summary" ? <TableHead className="py-2 pr-4 font-medium">Origin</TableHead> : null}
                    {boxSalesView !== "summary" ? <TableHead className="py-2 pr-4 font-medium">Product</TableHead> : null}
                    {boxSalesView !== "summary" ? <TableHead className="py-2 pr-4 font-medium">Process</TableHead> : null}
                    <TableHead className="py-2 pr-4 font-medium">Units</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Pounds</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Revenue</TableHead>
                    <TableHead className="py-2 font-medium">Avg $/lb</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentBoxRows.slice(0, 250).map((row: any, index) => {
                    const metrics = row.metrics ?? row;
                    return (
                      <TableRow key={row.cargoId || row.productId || row.originId || row.userId || row.bucket || index} className="border-b last:border-0">
                        {boxSalesView === "summary" ? <TableCell className="py-2 pr-4 font-medium">{row.bucket}</TableCell> : null}
                        {boxSalesView === "byClient" ? <TableCell className="py-2 pr-4">{row.clientName}</TableCell> : null}
                        {boxSalesView === "byClient" ? (
                          <TableCell className="py-2 pr-4">
                            <Badge variant="outline" className={getEnterpriseBadgeClass(row.isEnterprise)}>
                              {getEnterpriseBadgeLabel(row.isEnterprise)}
                            </Badge>
                          </TableCell>
                        ) : null}
                        {boxSalesView === "byOrigin" ? <TableCell className="py-2 pr-4">{row.originName}</TableCell> : null}
                        {boxSalesView === "byProduct" ? <TableCell className="py-2 pr-4">{row.productName}</TableCell> : null}
                        {boxSalesView === "byCargo" ? <TableCell className="py-2 pr-4 font-mono text-xs">{row.ociNumber}</TableCell> : null}

                        {boxSalesView !== "summary" ? (
                          <TableCell className="py-2 pr-4">{row.originName || "-"}</TableCell>
                        ) : null}
                        {boxSalesView !== "summary" ? (
                          <TableCell className="py-2 pr-4">{row.productName || "-"}</TableCell>
                        ) : null}
                        {boxSalesView !== "summary" ? (
                          <TableCell className="py-2 pr-4">{row.processType || "-"}</TableCell>
                        ) : null}
                        <TableCell className="py-2 pr-4">{toNumber(metrics.units).toLocaleString()}</TableCell>
                        <TableCell className="py-2 pr-4">{toNumber(metrics.pounds).toLocaleString()}</TableCell>
                        <TableCell className="py-2 pr-4">{formatMoney(metrics.revenue)}</TableCell>
                        <TableCell className="py-2">{formatMoney(metrics.avgPricePerLb)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "pricing" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Contract vs Spot by Cargo</CardTitle>
              <CardDescription>Direct cargo-level comparison for fairness checks.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportContractVsSpotByCargo} disabled={contractVsSpotByCargoRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Total Cargos"
              value={toNumber(contractVsSpotByCargo?.summary?.totalCargos).toLocaleString()}
              hint="All compared cargos"
              isLoading={isLoadingContractVsSpotByCargo}
            />
            <MetricCard
              title="Comparable"
              value={toNumber(contractVsSpotByCargo?.summary?.cargosWithBothTypes).toLocaleString()}
              hint="Have both contract + spot"
              isLoading={isLoadingContractVsSpotByCargo}
            />
            <MetricCard
              title="Favor Contracts"
              value={toNumber(contractVsSpotByCargo?.summary?.cargosFavoringContracts).toLocaleString()}
              hint="Contracts cheaper"
              isLoading={isLoadingContractVsSpotByCargo}
            />
            <MetricCard
              title="Total Savings"
              value={formatMoney(contractVsSpotByCargo?.summary?.totalContractSavings)}
              hint="Across comparable cargos"
              isLoading={isLoadingContractVsSpotByCargo}
            />
          </section>

          {isLoadingContractVsSpotByCargo ? (
            <SectionTableSkeleton rows={8} cols={7} />
          ) : contractVsSpotByCargoRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No cargo comparison data in this range.</p>
          ) : (
            <div className="max-h-[400px] overflow-auto">
              <Table className="text-sm">
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow className="border-b text-left text-muted-foreground hover:bg-transparent">
                    <TableHead className="py-2 pr-4 font-medium">OCI</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Product</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Contract $/lb</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Spot $/lb</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Diff $/lb</TableHead>
                    <TableHead className="py-2 pr-4 font-medium">Comparable</TableHead>
                    <TableHead className="py-2 font-medium">Verdict</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractVsSpotByCargoRows.slice(0, 250).map((row) => (
                    <TableRow key={row.cargoId} className="border-b last:border-0">
                      <TableCell className="py-2 pr-4 font-mono text-xs">{row.ociNumber}</TableCell>
                      <TableCell className="py-2 pr-4">{row.productName}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.contract?.avgPricePerLb)}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.spot?.avgPricePerLb)}</TableCell>
                      <TableCell className="py-2 pr-4">{formatMoney(row.comparison?.priceDifferencePerLb)}</TableCell>
                      <TableCell className="py-2 pr-4">
                        <Badge
                          variant="outline"
                          className={
                            row.comparison?.hasBothTypes
                              ? "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200"
                              : "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-300"
                          }
                        >
                          {row.comparison?.hasBothTypes ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground">{row.comparison?.interpretation || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {analyticsTab === "sampleImpact" ? (
      <Card className={PANEL_CARD_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Sample Impact Analysis</CardTitle>
              <CardDescription>
                Conversion and order lift within {sampleLookbackDays}d before and {sampleLookforwardDays}d after each sample.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportSampleImpact} disabled={sampleImpactRows.length === 0}>
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title="Samples Sent"
              value={sampleImpactSummary.totalSampleEvents.toLocaleString()}
              hint="Sample events analyzed"
              isLoading={isLoadingSampleImpact}
            />
            <MetricCard
              title="Clients Sampled"
              value={sampleImpactSummary.clientsSampled.toLocaleString()}
              hint="Unique sampled clients"
              isLoading={isLoadingSampleImpact}
            />
            <MetricCard
              title="Any Purchase Conv."
              value={`${sampleImpactSummary.anyPurchaseConversionRate.toFixed(0)}%`}
              hint="Bought anything after sample"
              isLoading={isLoadingSampleImpact}
            />
            <MetricCard
              title="Target Conv."
              value={`${sampleImpactSummary.targetPurchaseConversionRate.toFixed(0)}%`}
              hint="Bought sampled target"
              isLoading={isLoadingSampleImpact}
            />
            <MetricCard
              title="Avg Order Delta"
              value={sampleImpactSummary.avgOrderDelta.toFixed(2)}
              hint="Post window - pre window"
              isLoading={isLoadingSampleImpact}
            />
          </section>

          {!isLoadingSampleImpact ? (
            <div className="rounded border border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Avg target order delta: {sampleImpactSummary.avgTargetOrderDelta.toFixed(2)}
              {" · "}
              Avg days to first order: {sampleImpactSummary.avgDaysToFirstOrder ?? "N/A"}
              {" · "}
              Avg days to first target order: {sampleImpactSummary.avgDaysToTargetOrder ?? "N/A"}
              {" · "}
              No post-sample purchase: {sampleImpactSummary.noPurchaseAfterSample.toLocaleString()}
            </div>
          ) : null}

          {isLoadingSampleImpact ? (
            <SectionTableSkeleton rows={8} cols={9} />
          ) : sampleImpactRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No sample-impact data available for the selected filters.</p>
          ) : (
            <InternalEntityDataTable
              data={sampleImpactTableRows}
              columns={sampleImpactColumns}
              pageSize={25}
              enableSelection={false}
              initialSorting={[{ id: "sampleEventAt", desc: true }]}
            />
          )}
        </CardContent>
      </Card>
      ) : null}
    </div>
  );
}

function MetricCard({
  title,
  value,
  hint,
  isLoading = false,
}: {
  title: string;
  value: string;
  hint: string;
  isLoading?: boolean;
}) {
  return (
    <Card className={PANEL_CARD_CLASS}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{title}</p>
        {isLoading ? (
          <Skeleton className="mt-1 h-8 w-28" />
        ) : (
          <p className="mt-1 text-2xl font-bold">{value}</p>
        )}
        <p className="text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function SectionTableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }).map((__, colIndex) => (
            <Skeleton key={`${rowIndex}-${colIndex}`} className="h-7 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
